import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Radio, Play, Pause, Settings2 } from "lucide-react";
import { toast } from "sonner";

export const STATIONS = [
  { name: "RDS", url: "https://icstream.rds.radio/rds" },
  { name: "Radio 105", url: "https://icy.unitedradio.it/Radio105.mp3" },
  { name: "Virgin Radio", url: "https://icy.unitedradio.it/Virgin.mp3" },
  { name: "Radio Kiss Kiss", url: "https://ice07.fluidstream.net/KissKiss.aac" },
  { name: "Radio Paradise (Pop/Rock)", url: "https://stream.radioparadise.com/mp3-128" },
  { name: "SomaFM Groove Salad (Chill)", url: "https://ice1.somafm.com/groovesalad-128-mp3" },
];

export function RadioPlayer() {
  const { t } = useLanguage();
  const audioRef = useRef(null);
  const [station, setStation] = useState({ name: "", url: "" });
  const [playing, setPlaying] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", url: "" });
  const [volume, setVolume] = useState(() => Number(localStorage.getItem("radio_volume") ?? 0.7));

  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = { name: r.data.radio_name || "", url: r.data.radio_url || "" };
      setStation(s);
      setDraft(s);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    localStorage.setItem("radio_volume", String(volume));
  }, [volume]);

  const toggle = async () => {
    if (!station.url) { setOpen(true); return; }
    const a = audioRef.current;
    if (playing) { a.pause(); setPlaying(false); return; }
    try {
      a.src = station.url;
      await a.play();
      setPlaying(true);
    } catch (e) {
      toast.error(t("radio_error"));
    }
  };

  const save = async () => {
    try {
      await api.put("/settings", { radio_url: draft.url.trim(), radio_name: draft.name.trim() });
      setStation({ name: draft.name.trim(), url: draft.url.trim() });
      if (audioRef.current) { audioRef.current.pause(); setPlaying(false); }
      toast.success(t("settings_saved"));
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="radio-player" className="flex items-center gap-2 bg-white border border-[#E8DFD5] rounded-full pl-3 pr-1.5 py-1.5 shadow-sm">
      <audio ref={audioRef} preload="none" onError={() => { setPlaying(false); toast.error(t("radio_error")); }} />
      <Radio className={`w-4 h-4 ${playing ? "text-[#D97706] animate-pulse" : "text-[#786C66]"}`} />
      <span data-testid="radio-station-name" className="text-xs font-semibold text-[#4A2C2A] max-w-[120px] truncate">
        {station.name || t("radio_none")}
      </span>
      <input
        data-testid="radio-volume"
        type="range" min="0" max="1" step="0.05" value={volume}
        onChange={(e) => setVolume(Number(e.target.value))}
        className="w-16 accent-[#D97706] hidden sm:block"
        aria-label="volume"
      />
      <Button data-testid="radio-toggle-btn" size="icon" onClick={toggle} className="h-8 w-8 rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7]">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </Button>
      <Button data-testid="radio-settings-btn" size="icon" variant="ghost" onClick={() => { setDraft(station); setOpen(true); }} className="h-8 w-8 rounded-full text-[#786C66]">
        <Settings2 className="w-4 h-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FDFBF7] max-w-sm" data-testid="radio-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#4A2C2A]">{t("radio_title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {STATIONS.map((s) => (
              <button
                key={s.url}
                data-testid={`radio-preset-${s.name.replace(/\W+/g, "-").toLowerCase()}`}
                onClick={() => setDraft(s)}
                className={`w-full text-left text-sm rounded-xl px-3 py-2 border transition-colors ${draft.url === s.url ? "bg-[#4A2C2A] text-[#FDFBF7] border-[#4A2C2A]" : "bg-white border-[#E8DFD5] hover:border-[#D97706]/50"}`}
              >
                {s.name}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#786C66] mt-2">{t("radio_custom")}</p>
          <Input data-testid="radio-name-input" placeholder={t("radio_name_ph")} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="bg-white border-[#E8DFD5]" />
          <Input data-testid="radio-url-input" placeholder="https://..." value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} className="bg-white border-[#E8DFD5]" />
          <div className="flex gap-2 pt-2">
            <Button data-testid="save-radio-btn" onClick={save} className="flex-1 rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7]">{t("save")}</Button>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#E8DFD5]">{t("cancel")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
